import { useEffect, useRef } from 'react'
import Toast from 'react-native-toast-message'
import { useQueryClient } from '@tanstack/react-query'
import { useRegisterDevice } from './api/mutations/useRegisterDevice'
import { useCurrentUserStore } from '@store'
import { useLanguage } from '@context'
import { invalidateQueriesOnPush } from '@utils'
import { getWebFcmToken, onWebForegroundMessage } from '../services/firebaseWeb'
import { isViewingConversation, openPushTarget } from '../utils/pushNavigation'
import { deviceTimeZone, setCurrentPushToken } from '../utils/pushToken'

/**
 * Web FCM bootstrap. Mirrors the native useFcm hook contract:
 *
 * - asks browser notification permission
 * - fetches the FCM Web Push token (via VAPID) and registers it
 * - shows a toast when a notification arrives in the foreground (the
 *   browser does NOT show one itself unless the page is hidden); tapping it
 *   opens its screen
 *
 * No-op when:
 * - the user is not authenticated yet,
 * - Firebase Web env (FIREBASE_API_KEY, …, FIREBASE_VAPID_KEY) is not set,
 * - the browser does not support service workers / Push API,
 * - the user has previously denied notification permission.
 */
export const useFcm = () => {
  const { userID } = useCurrentUserStore()
  const { currentLanguage } = useLanguage()
  const { mutate: registerDevice } = useRegisterDevice()
  const queryClient = useQueryClient()
  const lastRegisteredToken = useRef<string | null>(null)

  useEffect(() => {
    if (!userID) return

    let unsubscribe: (() => void) | undefined
    let cancelled = false

    const bootstrap = async () => {
      const token = await getWebFcmToken()
      if (cancelled || !token) return

      if (lastRegisteredToken.current !== token) {
        lastRegisteredToken.current = token
        setCurrentPushToken(token)
        registerDevice({ token, timeZone: deviceTimeZone(), language: currentLanguage })
      }

      unsubscribe = onWebForegroundMessage((payload) => {
        const title = payload.notification?.title
        const body = payload.notification?.body
        const readingIt =
          payload.data?.type === 'NEW_MESSAGE' && isViewingConversation(payload.data?.conversationId)
        if ((title || body) && !readingIt) {
          Toast.show({
            type: 'info',
            text1: title ?? '',
            text2: body ?? '',
            onPress: () => {
              Toast.hide()
              void openPushTarget(payload.data)
            },
          })
        }
        // A push means the server changed data for this user (e.g. a shared
        // calendar event) without any local mutation, so refresh the lists
        // that would otherwise stay cached until staleTime.
        invalidateQueriesOnPush(queryClient, payload.data)
      })
    }

    void bootstrap()

    return () => {
      cancelled = true
      // The next account signing in on this browser registers it again.
      lastRegisteredToken.current = null
      unsubscribe?.()
    }
    // currentLanguage is read at registration time only, on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID, registerDevice, queryClient])
}
